'use client'

import type { ReactNode } from 'react'
import {
  EvaluationCriteriaSection,
  groupCriteriaResults,
  isV2AnalysisPayload,
  filterScoredCriteria,
} from '@/components/evaluation/evaluation-criteria-section'
import { EvaluationDimensionRow } from '@/components/evaluation/evaluation-dimension-row'
import { MatchQualityBadge } from '@/components/evaluation/match-quality-badge'
import { ScoreRing } from '@/components/evaluation/score-ring'
import { CandidateSkillsByDomain } from '@/components/evaluation/candidate-skills-by-domain'
import { AnalysisFindingsCards } from '@/components/evaluation/analysis-findings-cards'
import {
  RequirementsCoverageSection,
  resolveAnalysisRequirements,
  summarizeRequirements,
  matchingUnmappedSkillsForOther,
} from '@/components/evaluation/requirements-coverage-section'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { EvaluationAnalysisPayload } from '@/hooks/use-evaluation-analysis'

export type EvaluationAnalysisCopy = {
  /** Text after the percentage badge (default "match"). */
  scoreSuffix?: string
  highlightsTitle?: string
  redFlagsTitle?: string
}

export type EvaluationAnalysisDetailProps = {
  payload: EvaluationAnalysisPayload
  subjectLabel?: string
  copy?: EvaluationAnalysisCopy
  actions?: ReactNode
  depth?: 'full' | 'compact'
}

export function EvaluationAnalysisDetail({
  payload,
  subjectLabel,
  copy,
  actions,
  depth = 'full',
}: EvaluationAnalysisDetailProps) {
  const matchScore = payload.scoring_trace.final_score ?? 0
  const isV2 = isV2AnalysisPayload(payload)
  const grouped = groupCriteriaResults(payload.criteria_results)
  const scoredFocusAreas = filterScoredCriteria(grouped.focusAreas)
  const scoredDimensions = filterScoredCriteria(
    isV2 && grouped.dimensions.length > 0 ? grouped.dimensions : payload.dimensions,
  )
  const dimensions = scoredDimensions
  const candidateDomains = payload.candidate_skills_by_domain ?? []
  const { requirements: analysisRequirements } = resolveAnalysisRequirements(payload)
  const { metCount, knownCount } = summarizeRequirements(analysisRequirements)
  const otherSkills = matchingUnmappedSkillsForOther(payload, analysisRequirements)
  const showSkills =
    depth === 'full' &&
    isV2 &&
    payload.candidate_skills_by_domain !== undefined &&
    (candidateDomains.length > 0 || otherSkills.length > 0)
  const showFocusAreas = depth === 'full' && isV2 && scoredFocusAreas.length > 0
  const showDimensions = depth === 'full' && dimensions.length > 0
  const showScores = showFocusAreas || showDimensions
  const showRequirements = analysisRequirements.length > 0
  const compact = depth === 'compact'

  const requirementsSection =
    showRequirements ? (
      <Card>
        <CardContent className="p-0">
          {compact && knownCount > 0 ? (
            <p className="px-[var(--sp-card)] pt-[var(--sp-card)] text-xs text-muted-foreground">
              {metCount} of {knownCount} must-haves met
            </p>
          ) : null}
          <RequirementsCoverageSection requirements={analysisRequirements} />
        </CardContent>
      </Card>
    ) : null

  const findingsSection = (
    <AnalysisFindingsCards
      highlights={payload.findings.highlights}
      redFlags={payload.findings.red_flags}
      highlightsTitle={copy?.highlightsTitle}
      redFlagsTitle={copy?.redFlagsTitle}
      twoColumn
    />
  )

  const narrativeSection =
    compact && payload.narrative.summary ? (
      <Card>
        <CardContent className="pt-[var(--sp-card)]">
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
            {payload.narrative.summary}
          </p>
        </CardContent>
      </Card>
    ) : null

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="grid gap-5 p-0 sm:grid-cols-[auto_1fr] sm:items-center">
          <ScoreRing
            score={matchScore}
            caption={copy?.scoreSuffix ?? 'match'}
            className="mx-auto sm:mx-0"
          />
          <div className="space-y-2">
            {subjectLabel ? <p className="text-sm text-muted-foreground">{subjectLabel}</p> : null}
            <MatchQualityBadge score={matchScore} showPercent={false} />
            <h2 className="text-lg font-semibold">{payload.headline}</h2>
            {!compact && payload.narrative.summary ? (
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {payload.narrative.summary}
              </p>
            ) : null}
            {actions ? <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div> : null}
          </div>
        </CardContent>
      </Card>

      {compact ? (
        <>
          {requirementsSection}
          {findingsSection}
          {narrativeSection}
        </>
      ) : (
        <>
          {findingsSection}
          {requirementsSection}
        </>
      )}

      {showSkills ? (
        <Card>
          <CardContent className="p-0">
            <CandidateSkillsByDomain
              domains={candidateDomains}
              unmapped={otherSkills}
              showOther
            />
          </CardContent>
        </Card>
      ) : null}

      {showScores ? (
        <Card>
          <CardHeader className="p-0 pb-3">
            <CardTitle className="text-sm font-semibold">How we scored</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-0">
            {showFocusAreas ? (
              <EvaluationCriteriaSection
                title="Role focus"
                criteria={scoredFocusAreas}
              />
            ) : null}
            {showDimensions ? (
              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Match factors
                </h3>
                {dimensions.map((dimension) => (
                  <EvaluationDimensionRow
                    key={dimension.key}
                    label={dimension.label}
                    score={dimension.score as number}
                    reasoning={dimension.reasoning}
                  />
                ))}
              </section>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {depth === 'full' && (payload.methodology_id || payload.scoring_trace.was_llm_judged) ? (
        <p className="text-xs text-muted-foreground">
          {[
            payload.methodology_id ? payload.methodology_id.replace(/_/g, ' ') : null,
            payload.scoring_trace.was_llm_judged ? 'LLM-judged' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}

      {depth === 'full' && payload.disclaimer ? (
        <p className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          {payload.disclaimer}
        </p>
      ) : null}
    </div>
  )
}
